import { FormattedMessage } from '@alltrails/shared/react-intl';
import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import UpsellTrigger from '@alltrails/analytics/enums/UpsellTrigger';
import useUser from 'hooks/useUser';
import logUpsellMidpointShown from '@alltrails/analytics/events/logUpsellMidpointShown';
import Typography from '@alltrails/shared/denali/components/Typography';
import CheckSm from '@alltrails/shared/icons/CheckSm';
import PurchaseScreenTypePlan from '@alltrails/analytics/enums/PurchaseScreenTypePlan';
import useExperiments from 'hooks/useExperiments';
import * as styles from './PeakFeatureList.module.scss';

const baseFeatures = [
  { base: true, plus: true, peak: true, text: <FormattedMessage defaultMessage="Save trails and create lists " /> },
  { base: true, plus: true, peak: true, text: <FormattedMessage defaultMessage="Navigate to stay on route" /> }
];

const newPlusFeatures = [
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Download offline maps" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Plan for on-trail conditions" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Tour photos along the trail" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Get alerts for wrong turns" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Preview trails and terrain in 3D" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Share live activities with friends" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Find trails by distance from you" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Print maps for backup" /> },
  { base: false, plus: true, peak: true, text: <FormattedMessage defaultMessage="Go ad-free" /> }
];

const byotFreePeakFeatures = [
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Explore community heatmaps" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Build your own routes in app" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Customize AllTrails routes in app" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Identify plants and more" /> }
];

const conditionsPlusPeakFeatures = [
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Build your own routes" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Customize AllTrails routes" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Explore community heatmaps" /> },
  { base: false, plus: false, peak: true, text: <FormattedMessage defaultMessage="Identify plants and more" /> }
];

type Props = {
  tierSelected: 'base' | 'plus' | 'peak';
  disableMidpointTracking?: boolean;
  upsellTrigger?: UpsellTrigger;
  className?: string;
  hasHeaderScrollMargin?: boolean;
};

// Design File https://www.figma.com/design/PtYSSpohQ9rL7ZWCcdsYJK/2025-Upsell-System?node-id=5541-181584&m=dev
export default function PeakFeatureListTable({
  upsellTrigger,
  tierSelected = 'peak',
  disableMidpointTracking = false,
  className,
  hasHeaderScrollMargin
}: Props) {
  const midpointRef = useRef<HTMLTableRowElement>(null);
  const user = useUser();
  const isPromoEligible = !user || user.isPromoEligible;
  const experiments = useExperiments();

  const isConditionsPlusEnabled = experiments['rm-conditions-plus']?.value === 'on';

  const getPeakFeatures = () => {
    if (isConditionsPlusEnabled) return conditionsPlusPeakFeatures;
    return byotFreePeakFeatures;
  };

  const plusFeatures = newPlusFeatures;
  const peakFeatures = getPeakFeatures();
  const features = [...baseFeatures, ...plusFeatures, ...peakFeatures];

  useEffect(() => {
    if (disableMidpointTracking) return;

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          logUpsellMidpointShown({
            purchase_screen_type_plan: isPromoEligible ? PurchaseScreenTypePlan.FreeTrial : PurchaseScreenTypePlan.NonTrial,
            trigger: upsellTrigger
          });
        }
      });
    });

    if (midpointRef.current) {
      observer.observe(midpointRef.current);
    }

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [disableMidpointTracking, isPromoEligible, midpointRef, upsellTrigger, user]);

  return (
    <table
      id="feature-list"
      className={classNames(styles.table, className, {
        [styles.peakSelected]: tierSelected === 'peak',
        [styles.plusSelected]: tierSelected === 'plus',
        [styles.baseSelected]: tierSelected === 'base',
        [styles.headerScrollMargin]: hasHeaderScrollMargin
      })}
    >
      <thead>
        <tr>
          <th>
            <FormattedMessage defaultMessage="What you get" />
          </th>
          <th>Base</th>
          <th>Plus</th>
          <th>Peak</th>
        </tr>
      </thead>
      <tbody>
        {features.map((feature, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={index} {...(index === 7 ? { ref: midpointRef } : {})}>
            <td>
              <Typography variant="text100">{feature.text}</Typography>
            </td>
            <td>{feature.base && <CheckSm color={tierSelected === 'base' ? '--color-text-primary' : '--color-text-tertiary'} />}</td>
            <td>{feature.plus && <CheckSm color={tierSelected === 'plus' ? '--color-text-primary' : '--color-text-tertiary'} />}</td>
            <td>
              <CheckSm color={tierSelected === 'peak' ? '--color-text-primary' : '--color-text-tertiary'} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
